
import { Component, Vue } from "vue-property-decorator";
import BatchFilter, { BatchFilterData } from "./components/BatchFilter.vue";
import BatchRequestList from "./components/BatchRequestList.vue";
import BatchClaimList from "./components/BatchClaimList.vue";

const PENDING_STATE_ID = 1;

@Component({
    components: {
        BatchFilter,
        BatchRequestList,
        BatchClaimList,
        Modal: () => import("@/components/Modal.vue")
    }
})
export default class BatchForm extends Vue {
    [x: string]: any;

    batch: any = null;
    isSubmitting: boolean = false;
    searched: boolean = false;
    activeTab: "requests" | "claims" = "requests";

    filter: BatchFilterData = { dateFrom: "", dateTo: "", includeUnprocessed: false };
    activeFilter: BatchFilterData = { dateFrom: "", dateTo: "", includeUnprocessed: false };

    requests: any[] = [];
    requestsTotalRows: number = 0;
    requestsSort: any = [{ col: "requests.batchId", dir: "DESC" }, { col: "requests.createdAt", dir: "DESC" }];
    requestsLimit: number = 10;
    loadingRequests: boolean = false;

    claims: any[] = [];
    claimsTotalRows: number = 0;
    claimsSort: any = [{ col: "claims.batchId", dir: "DESC" }, { col: "claims.createdAt", dir: "DESC" }];
    claimsLimit: number = 10;
    loadingClaims: boolean = false;

    statuses: any[] = [];
    selectedRequestIds: number[] = [];
    selectedClaimIds: number[] = [];
    requestsAllIds: number[] = [];
    claimsAllIds: number[] = [];
    pendingFilter: BatchFilterData | null = null;

    get batchId(): string | null {
        return (this.$route.params as any).id || null;
    }

    get isCreate(): boolean {
        return !this.batchId;
    }

    get hasBatch(): boolean {
        return !!this.batchId && !!this.batch;
    }

    get isPendingBatch(): boolean {
        return this.hasBatch && this.batch.state && this.batch.state.id === PENDING_STATE_ID;
    }

    get isEdit(): boolean {
        return this.isPendingBatch;
    }

    get isView(): boolean {
        return this.hasBatch && !this.isPendingBatch;
    }

    get pageTitle(): string {
        if (this.isView) return `Batch ${(this.batch && this.batch.code) || ""}`;
        if (this.isEdit) return `Editar Batch ${(this.batch && this.batch.code) || ""}`;
        return "Crear Batch";
    }

    get confirmModalText(): string {
        const totalRequests = this.selectedRequestIds.length;
        const totalClaims = this.selectedClaimIds.length;

        const requestText = totalRequests === 1 ? "1 solicitud" : `${totalRequests} solicitudes`;
        const claimText = totalClaims === 1 ? "1 siniestro" : `${totalClaims} siniestros`;

        const action = this.isEdit ? "actualizar" : "crear";
        
        let itemsText = "";
        if (totalRequests > 0 && totalClaims > 0) {
            itemsText = `${requestText} y ${claimText}`;
        } else if (totalRequests > 0) {
            itemsText = requestText;
        } else {
            itemsText = claimText;
        }

        return `Está por ${action} un batch con ${itemsText}.<br/><br/>¿Desea continuar?`;
    }

    get successMessage(): string {

        const editSuccess = "El batch fue actualizado correctamente.";
        const createSuccess = "La creación del batch fue iniciada correctamente. <br/>Puede ver el estado en la pantalla de batches.";

        return this.isEdit ? editSuccess : createSuccess;
    }

    async mounted() {
        await this.loadStatuses();
        if (this.batchId) {
            await this.loadBatch();
        }
    }

    async loadStatuses() {
        try {
            const response: any = await (this.$root as any)._get("requests/status");
            this.statuses = response || [];
        } catch (e) {
            (this.$root as any).notify("No se pudieron cargar los estados.", "error");
        }
    }

    async loadBatch() {
        try {
            const response: any = await (this.$root as any)._get(`batches/${this.batchId}`);
            this.batch = response;
            this.filter = {
                dateFrom: response.dateFrom || "",
                dateTo: response.dateTo || "",
                includeUnprocessed: response.includeUnprocessed || false
            };
            this.activeFilter = { ...this.filter };
            this.loadingRequests = true;
            this.loadingClaims = true;
            this.searched = true;
            await Promise.all([
                this.loadRequestIds(),
                this.loadClaimIds(),
                this.loadRequests(1, this.requestsSort, true),
                this.loadClaims(1, this.claimsSort, true)
            ]);
            this.selectedRequestIds = response.request_ids || [];
            this.selectedClaimIds = response.claim_ids || [];
        } catch (e) {
            (this.$root as any).notify("No se pudo cargar el batch. Intente nuevamente.", "error");
        }
    }

    get apiFilter(): Record<string, any> {
        const procesadaIdx = this.statuses.indexOf("Procesada");
        const noProcesadaIdx = this.statuses.indexOf("No Procesada");

        if (this.isView && this.batchId) {
            return {
                batchId: this.batchId
            };
        }

        const filter: Record<string, any> = {
            batchStateId: "-1",
            dateFrom: this.activeFilter.dateFrom,
            dateTo: this.activeFilter.dateTo,
        };

        if (this.batchId) {
            filter.batchId = this.batchId;
        }

        if (procesadaIdx > -1) {
            filter.status = this.activeFilter.includeUnprocessed && noProcesadaIdx > -1
                ? [procesadaIdx, noProcesadaIdx]
                : procesadaIdx;
        }

        return filter;
    }

    async loadRequestIds(showLoading: boolean = false) {
        if (showLoading) this.loadingRequests = true;
        try {
            const response: any = await (this.$root as any)._post("requests/ids", {
                order: this.requestsSort,
                models: "companies",
                ...this.apiFilter
            });
            this.requestsAllIds = response.data || [];
        } catch (e) {
            this.requestsAllIds = [];
        }
    }

    async loadRequests(page: number, sort: any, showLoading: boolean = false) {
        this.requestsSort = sort;
        if (showLoading) this.loadingRequests = true;
        try {
            const response: any = await (this.$root as any)._post("requests/all", {
                limit: this.requestsLimit,
                page,
                order: sort,
                models: "companies",
                ...this.apiFilter
            });
            this.requests = response.data;
            this.requestsTotalRows = response.totalRows;
        } catch (e) {
            (this.$root as any).notify("No se pudieron cargar las solicitudes.", "error");
        } finally {
            this.loadingRequests = false;
        }
    }

    async loadClaimIds(showLoading: boolean = false) {
        if (showLoading) this.loadingClaims = true;
        try {
            const response: any = await (this.$root as any)._post("claims/ids", {
                order: this.claimsSort,
                ...this.apiFilter
            });
            this.claimsAllIds = response.data || [];
        } catch (e) {
            this.claimsAllIds = [];
        }
    }

    async loadClaims(page: number, sort: any, showLoading: boolean = false) {
        this.claimsSort = sort;
        if (showLoading) this.loadingClaims = true;
        try {
            const response: any = await (this.$root as any)._post("claims/all", {
                limit: this.claimsLimit,
                page,
                order: sort,
                ...this.apiFilter
            });
            this.claims = response.data;
            this.claimsTotalRows = response.totalRows;
        } catch (e) {
            (this.$root as any).notify("No se pudieron cargar los siniestros.", "error");
        } finally {
            this.loadingClaims = false;
        }
    }

    async onSearch(newFilter: BatchFilterData) {
        const hasSelection = this.selectedRequestIds.length > 0 || this.selectedClaimIds.length > 0;

        if (hasSelection && this.searched) {
            this.pendingFilter = newFilter;
            (this.$refs.searchAlert as any).show();
            return;
        }

        await this.executeSearch(newFilter);
    }

    async confirmSearch() {
        (this.$refs.searchAlert as any).hide();
        if (this.pendingFilter) {
            await this.executeSearch(this.pendingFilter);
            this.pendingFilter = null;
        }
    }

    async executeSearch(newFilter: BatchFilterData) {
        this.selectedRequestIds = [];
        this.selectedClaimIds = [];
        this.activeFilter = { ...newFilter };
        this.filter = { ...newFilter };
        this.searched = true;
        await Promise.all([
            this.loadRequestIds(true),
            this.loadClaimIds(true)
        ]);
        await Promise.all([
            this.loadRequests(1, this.requestsSort, true),
            this.loadClaims(1, this.claimsSort, true)
        ]);
    }

    onClear() {
        this.requests = [];
        this.claims = [];
        this.requestsTotalRows = 0;
        this.claimsTotalRows = 0;
        this.requestsAllIds = [];
        this.claimsAllIds = [];
        this.selectedRequestIds = [];
        this.selectedClaimIds = [];
        this.searched = false;
    }

    onRequestsSortChange(sort: any) {
        this.loadRequests(1, sort, false);
    }

    onRequestsPageChange(page: number, limit: number) {
        this.requestsLimit = limit;
        this.loadRequests(page, this.requestsSort, false);
    }

    onClaimsSortChange(sort: any) {
        this.loadClaims(1, sort, false);
    }

    onClaimsPageChange(page: number, limit: number) {
        this.claimsLimit = limit;
        this.loadClaims(page, this.claimsSort, false);
    }

    goBack() {
        this.$router.push('/admin/batches');
    }

    confirmSubmit() {
        if (this.selectedRequestIds.length === 0 && this.selectedClaimIds.length === 0) {
            (this.$root as any).notify(
                "Debe seleccionar al menos una solicitud o siniestro.",
                "error"
            );
            return;
        }
        (this.$refs.confirmModal as any).show();
    }

    async submitBatch() {
        (this.$refs.confirmModal as any).hide();
        (this.$refs.processingModal as any).show();
        this.isSubmitting = true;
        try {
            const payload = {
                date_from: this.activeFilter.dateFrom,
                date_to: this.activeFilter.dateTo,
                include_unprocessed: this.activeFilter.includeUnprocessed,
                request_ids: this.selectedRequestIds,
                claims_ids: this.selectedClaimIds
            };
            if (this.isEdit) {
                await (this.$root as any)._patch(`batches/${this.batchId}`, payload);
            } else {
                await (this.$root as any)._post("batches", payload);
            }
            (this.$refs.processingModal as any).hide();
            (this.$refs.successModal as any).show();
        } catch (e) {
            (this.$refs.processingModal as any).hide();
            (this.$root as any).notify("No se pudo procesar el batch. Intente más tarde.", "error");
        } finally {
            this.isSubmitting = false;
        }
    }
}
